import type { Dispatch, SetStateAction } from 'react';

import { Button } from 'design-system';
import { ChevronDownIcon } from 'lucide-react';
import { Link } from 'react-router';

import type { MonthlyRecapsArticle } from '@/features/monthly-recaps';

import type { FeedInfo } from '../types';
import { FeedArticleModalLink } from './FeedArticleModalLink';

type FeedCtaProps = Pick<FeedInfo['values'], 'CTAValue' | 'CTAType' | 'CTALabel'> & {
  open: boolean;
  setOpen: Dispatch<SetStateAction<boolean>>;
  article?: MonthlyRecapsArticle;
};

const CTA_LINK_CONFIG = {
  Catalog: { label: 'See in catalog', path: 'catalog' },
  Market: { label: 'Go to market', path: 'markets' }
} as const;

export const FeedCta = ({ CTAType, CTAValue, CTALabel, open, setOpen, article }: FeedCtaProps) => {
  if (!CTAType) return null;

  if (CTAType === 'Catalog' || CTAType === 'Market') {
    const { label, path } = CTA_LINK_CONFIG[CTAType];

    return (
      <Link
        className='text-highlight underline-offset-4 hover:underline'
        to={`../${path}/${CTAValue}`}
      >
        {label}
      </Link>
    );
  }

  if (CTAType === 'Symbol') {
    return (
      <Button
        aria-expanded={open}
        variant='link'
        onClick={() => {
          setOpen(value => !value);
        }}
      >
        See codes
        <ChevronDownIcon
          aria-hidden='true'
          className={open ? 'rotate-180' : undefined}
        />
      </Button>
    );
  }

  if (CTAType === 'Article') {
    if (!article) {
      return null;
    }

    return (
      <FeedArticleModalLink
        CTALabel={CTALabel}
        article={article}
      />
    );
  }

  return (
    <a
      className='text-highlight underline-offset-4 hover:underline'
      href={CTAValue}
      rel='noreferrer noopener'
      target='_blank'
    >
      {CTALabel ?? 'Read more'}
    </a>
  );
};
